import type { Adres, Woning } from '@/types/domain';
import { PLANGEBIED_BBOX, ligtInPlangebied } from '@/data/plangebied';
import { isUitgesloten } from '@/data/uitgeslotenWoningen';
import { ServiceFout, type WoningService } from './types';

/**
 * Live BAG-bron via PDOK.
 *
 * - Kaartobjecten komen uit de BAG WFS v2 (verblijfsobjecten).
 * - Zoeken en omgekeerd zoeken gaan via de Locatieserver.
 *
 * Beide diensten zijn open en vereisen geen sleutel.
 */

const LOCATIESERVER = 'https://api.pdok.nl/bzk/locatieserver/search/v3_1';
const BAG_WFS = 'https://service.pdok.nl/lv/bag/wfs/v2_0';

/** Aantal objecten per WFS-aanroep. Meer vragen levert een trage response op. */
const PAGINA = 1000;

/** Bovengrens tegen een oneindige lus als de dienst zich onverwacht gedraagt. */
const MAX_PAGINAS = 20;

interface LocatieserverDoc {
  id?: string;
  weergavenaam?: string;
  straatnaam?: string;
  huisnummer?: number;
  huisletter?: string;
  huisnummertoevoeging?: string;
  postcode?: string;
  woonplaatsnaam?: string;
  centroide_ll?: string;
  adresseerbaarobject_id?: string;
  nummeraanduiding_id?: string;
}

/** "POINT(4.857 51.700)" -> [lat, lon] */
function parsePoint(wkt: string | undefined): [number, number] | null {
  if (!wkt) return null;
  const match = wkt.match(/POINT\(([-\d.]+)\s+([-\d.]+)\)/i);
  if (!match) return null;
  return [Number(match[2]), Number(match[1])];
}

/** De diensten leveren "4931CV"; overal elders in de app staat "4931 CV". */
function formatteerPostcode(postcode: string): string {
  const schoon = postcode.replace(/\s+/g, '').toUpperCase();
  return /^\d{4}[A-Z]{2}$/.test(schoon) ? `${schoon.slice(0, 4)} ${schoon.slice(4)}` : postcode;
}

function docNaarWoning(doc: LocatieserverDoc): Woning | null {
  const punt = parsePoint(doc.centroide_ll);
  if (!punt) return null;

  const adres: Adres = {
    straat: doc.straatnaam ?? '',
    huisnummer: doc.huisnummer !== undefined ? String(doc.huisnummer) : '',
    huisletter: doc.huisletter || undefined,
    toevoeging: doc.huisnummertoevoeging || undefined,
    postcode: formatteerPostcode(doc.postcode ?? ''),
    plaats: doc.woonplaatsnaam ?? '',
  };

  return {
    bagId: doc.adresseerbaarobject_id ?? doc.nummeraanduiding_id ?? doc.id ?? '',
    adres,
    latitude: punt[0],
    longitude: punt[1],
  };
}

async function haalJson<T>(url: string, signal?: AbortSignal): Promise<T> {
  const response = await fetch(url, { signal, headers: { Accept: 'application/json' } });
  if (!response.ok) {
    throw new ServiceFout(`PDOK gaf status ${response.status} voor ${url}`);
  }
  return (await response.json()) as T;
}

/* ------------------------------------------------------------------ */
/* WFS-eigenschappen                                                   */
/* ------------------------------------------------------------------ */

type Eigenschappen = Record<string, unknown>;

/**
 * De PDOK BAG WFS heeft in de loop der jaren verschillende veldnamen gebruikt.
 * We proberen de bekende varianten in volgorde, zodat een schemawijziging de
 * kaart niet meteen breekt.
 */
function pak(props: Eigenschappen, ...namen: string[]): string {
  for (const naam of namen) {
    const waarde = props[naam];
    if (waarde !== undefined && waarde !== null && waarde !== '') return String(waarde);
  }
  return '';
}

/**
 * PDOK levert bij EPSG:4326 coordinaten in volgorde lat,lon terwijl GeoJSON
 * lon,lat voorschrijft. In Nederland is de breedtegraad (50-54) altijd groter
 * dan de lengtegraad (3-8), dus we kunnen de volgorde betrouwbaar afleiden.
 */
function normaliseerCoordinaat(coords: number[]): [number, number] {
  const [a, b] = coords;
  return a > b ? [a, b] : [b, a];
}

function eersteCoordinaat(geometry: unknown): [number, number] | null {
  const geo = geometry as { type?: string; coordinates?: unknown };
  if (!geo?.coordinates) return null;

  let huidig: unknown = geo.coordinates;
  while (Array.isArray(huidig) && Array.isArray(huidig[0])) {
    huidig = huidig[0];
  }
  if (Array.isArray(huidig) && typeof huidig[0] === 'number' && typeof huidig[1] === 'number') {
    return normaliseerCoordinaat(huidig as number[]);
  }
  return null;
}

interface WfsFeature {
  properties?: Eigenschappen;
  geometry?: unknown;
}

function featureNaarWoning(feature: WfsFeature): Woning | null {
  const props = feature.properties ?? {};
  const punt = eersteCoordinaat(feature.geometry);
  if (!punt) return null;

  // Een verblijfsobject kan ook een winkel, garagebox of kantoor zijn. Voor
  // afkoppelen zijn alleen woningen interessant. Bij gemengd gebruik staat er
  // meer dan een doel, vandaar de zoektocht binnen de tekst.
  const gebruiksdoel = pak(props, 'gebruiksdoel').toLowerCase();
  if (gebruiksdoel && !gebruiksdoel.includes('woonfunctie')) return null;

  const straat = pak(props, 'openbare_ruimte', 'openbare_ruimte_naam', 'straatnaam');
  const huisnummer = pak(props, 'huisnummer');
  if (!straat && !huisnummer) return null;

  return {
    bagId: pak(props, 'identificatie', 'verblijfsobject_id', 'id'),
    adres: {
      straat,
      huisnummer,
      huisletter: pak(props, 'huisletter') || undefined,
      toevoeging: pak(props, 'toevoeging', 'huisnummertoevoeging') || undefined,
      postcode: formatteerPostcode(pak(props, 'postcode')),
      plaats: pak(props, 'woonplaats', 'woonplaatsnaam'),
    },
    latitude: punt[0],
    longitude: punt[1],
    bouwjaar: Number(pak(props, 'bouwjaar')) || undefined,
    oppervlakteM2: Number(pak(props, 'oppervlakte')) || undefined,
  };
}

/* ------------------------------------------------------------------ */
/* Service                                                             */
/* ------------------------------------------------------------------ */

export function maakPdokWoningService(): WoningService {
  // Het plangebied verandert niet tijdens een sessie, dus één keer ophalen.
  let inGeheugen: Promise<Woning[]> | null = null;

  async function haalPagina(startIndex: number): Promise<WfsFeature[]> {
    const { zuid, west, noord, oost } = PLANGEBIED_BBOX;

    // WFS 2.0 met EPSG:4326 verwacht de bbox in volgorde zuid,west,noord,oost.
    const params = new URLSearchParams({
      service: 'WFS',
      version: '2.0.0',
      request: 'GetFeature',
      typeNames: 'bag:verblijfsobject',
      outputFormat: 'application/json',
      srsName: 'EPSG:4326',
      count: String(PAGINA),
      startIndex: String(startIndex),
      bbox: `${zuid},${west},${noord},${oost},urn:ogc:def:crs:EPSG::4326`,
    });

    const data = await haalJson<{ features?: WfsFeature[] }>(`${BAG_WFS}?${params.toString()}`);
    return data.features ?? [];
  }

  async function haalAlles(): Promise<Woning[]> {
    const woningen: Woning[] = [];

    for (let pagina = 0; pagina < MAX_PAGINAS; pagina += 1) {
      const features = await haalPagina(pagina * PAGINA);

      for (const feature of features) {
        const woning = featureNaarWoning(feature);
        // De omhullende rechthoek is ruimer dan het gebied zelf; hier valt af
        // wat er buiten de werkelijke grens ligt. Daarna gaan de adressen eruit
        // die het project bewust niet meeneemt.
        if (
          woning &&
          ligtInPlangebied(woning.latitude, woning.longitude) &&
          !isUitgesloten(woning.adres)
        ) {
          woningen.push(woning);
        }
      }

      if (features.length < PAGINA) break;
    }

    return woningen;
  }

  return {
    async haalWoningen(): Promise<Woning[]> {
      if (!inGeheugen) {
        inGeheugen = haalAlles().catch((fout) => {
          // Bij een fout niet het mislukte resultaat vasthouden, anders blijft
          // de kaart leeg tot een herlaad.
          inGeheugen = null;
          throw new ServiceFout('BAG-woningen konden niet worden opgehaald.', fout);
        });
      }
      return inGeheugen;
    },

    /**
     * Zoeken op adres, met het plangebied vooraan.
     *
     * De adresdienst van PDOK zoekt heel Nederland af. Een straatnaam als
     * Ranonkelstraat komt in tientallen plaatsen voor, en dan opent een coach
     * zomaar een woning in een andere gemeente en begint daar een registratie.
     * Adressen binnen het plangebied staan daarom altijd bovenaan; de rest
     * volgt eronder, zodat zoeken buiten het gebied nog steeds kan.
     */
    async zoek(term: string): Promise<Woning[]> {
      const schoon = term.trim();
      if (schoon.length < 2) return [];

      const params = new URLSearchParams({
        q: schoon,
        fq: 'type:adres',
        rows: '30',
        fl: 'id,weergavenaam,straatnaam,huisnummer,huisletter,huisnummertoevoeging,postcode,woonplaatsnaam,centroide_ll,adresseerbaarobject_id,nummeraanduiding_id',
      });

      try {
        const data = await haalJson<{ response?: { docs?: LocatieserverDoc[] } }>(
          `${LOCATIESERVER}/free?${params.toString()}`
        );

        const gevonden = (data.response?.docs ?? [])
          .map(docNaarWoning)
          .filter((w): w is Woning => w !== null);

        const binnen = gevonden.filter((w) => ligtInPlangebied(w.latitude, w.longitude));
        const buiten = gevonden.filter((w) => !ligtInPlangebied(w.latitude, w.longitude));

        return [...binnen, ...buiten].slice(0, 20);
      } catch (fout) {
        throw new ServiceFout('Zoeken bij PDOK is niet gelukt.', fout);
      }
    },

    async adresBijPunt(latitude: number, longitude: number): Promise<Woning | null> {
      const params = new URLSearchParams({
        lat: String(latitude),
        lon: String(longitude),
        rows: '1',
        fq: 'type:adres',
        fl: 'id,weergavenaam,straatnaam,huisnummer,huisletter,huisnummertoevoeging,postcode,woonplaatsnaam,centroide_ll,adresseerbaarobject_id,nummeraanduiding_id',
      });

      try {
        const data = await haalJson<{ response?: { docs?: LocatieserverDoc[] } }>(
          `${LOCATIESERVER}/reverse?${params.toString()}`
        );
        const doc = data.response?.docs?.[0];
        return doc ? docNaarWoning(doc) : null;
      } catch {
        // Geen adres kunnen vinden mag het plaatsen van een punt niet blokkeren.
        return null;
      }
    },
  };
}
